'use client';

import type { CSSProperties } from 'react';
import type { CategoryPanel } from './homepage-types';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useId, useState } from 'react';
import { PillLink } from './ui/pill-link';
import { ScrollArrows, useScrollArrows } from './ui/scroll-arrows';

type CategoryExplorerProps = {
  panels: CategoryPanel[];
  wholeCategoryLabel: string;
  previousLabel: string;
  nextLabel: string;
};

const colorVariable = (color: string) => ({ '--c': color }) as CSSProperties;

/** Fades the tabs out under the arrows, on the sides where there are more of them. */
const EDGE_FADE = {
  both: '[mask-image:linear-gradient(to_right,transparent,#000_64px,#000_calc(100%_-_64px),transparent)]',
  start: '[mask-image:linear-gradient(to_right,transparent,#000_64px)]',
  end: '[mask-image:linear-gradient(to_right,#000_calc(100%_-_64px),transparent)]',
};

export function CategoryExplorer({
  panels,
  wholeCategoryLabel,
  previousLabel,
  nextLabel,
}: CategoryExplorerProps) {
  const [active, setActive] = useState(0);
  const baseId = useId();
  const { ref, canScroll, scroll } = useScrollArrows<HTMLDivElement>();

  return (
    <>
      <div className="relative mb-6">
        {/* The scroll padding keeps a tab paged or clicked into view clear of the arrows. */}
        <div
          ref={ref}
          role="tablist"
          className={cn(
            'relative -my-1 flex scroll-px-14 gap-2 overflow-x-auto py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            canScroll.previous && canScroll.next
              ? EDGE_FADE.both
              : canScroll.previous
                ? EDGE_FADE.start
                : canScroll.next && EDGE_FADE.end,
          )}
        >
          {panels.map((panel, index) => (
            <button
              key={panel.id}
              id={`${baseId}-tab-${index}`}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-controls={`${baseId}-panel-${index}`}
              onClick={event => {
                setActive(index);
                event.currentTarget.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                  inline: 'nearest',
                });
              }}
              style={colorVariable(panel.color)}
              className={cn(
                'flex flex-none cursor-pointer items-center gap-2 rounded-full border-[1.5px] border-[#e6eaee] bg-white py-[9px] pl-2.5 pr-4 font-semibold',
                index === active && 'border-[var(--c)] bg-[var(--c)] text-white',
              )}
            >
              {panel.icon && (
                <Image
                  src={panel.icon.url}
                  alt=""
                  width={28}
                  height={28}
                  className="h-7 w-7 rounded-full bg-[var(--c)] p-[5px]"
                />
              )}
              {panel.name}
            </button>
          ))}
        </div>
        <ScrollArrows
          canScroll={canScroll}
          onScroll={scroll}
          previousLabel={previousLabel}
          nextLabel={nextLabel}
          className="top-1/2"
        />
      </div>
      {panels.map((panel, index) => (
        <div
          key={panel.id}
          id={`${baseId}-panel-${index}`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${index}`}
          style={colorVariable(panel.color)}
          className={cn(
            'hidden grid-cols-1 gap-5 min-[1025px]:grid-cols-[340px_1fr]',
            index === active && 'grid',
          )}
        >
          <div className="flex flex-col items-start justify-between rounded-[22px] border-t-[6px] border-[var(--c)] bg-[#f3f5f7] p-[22px]">
            {panel.image && (
              <div className="relative mb-4 h-[180px] w-[180px] max-w-full self-center min-[1025px]:h-[240px] min-[1025px]:w-[240px]">
                <Image
                  src={panel.image.url}
                  alt={panel.image.alt}
                  fill
                  sizes="(min-width: 1025px) 240px, 180px"
                  quality={75}
                  className="object-contain mix-blend-multiply"
                />
              </div>
            )}
            <PillLink href={panel.href}>{`${wholeCategoryLabel} ${panel.name} →`}</PillLink>
          </div>
          <div className="grid grid-cols-2 gap-2.5 min-[641px]:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
            {panel.children.map(child => (
              <Link
                key={child.id}
                href={child.href}
                prefetch={false}
                className="flex flex-col gap-1 rounded-2xl border border-[#e6eaee] p-3.5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[var(--c)]"
              >
                <span className="h-[46px]">
                  {child.icon && (
                    <Image
                      src={child.icon.url}
                      alt=""
                      width={46}
                      height={46}
                      className="h-[46px] w-[46px] object-contain"
                    />
                  )}
                </span>
                <b className="text-[.92rem]">{child.name}</b>
                {child.countLabel && (
                  <small className="text-[.78rem] text-[#5f6b76]">{child.countLabel}</small>
                )}
              </Link>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}
